import React, { useState, useRef, useEffect } from 'react';
import { useAccessibility } from '../../context/AccessibilityContext/AccessibilityContext';
import Lightbox from 'react-18-image-lightbox';
import 'react-18-image-lightbox/style.css';
import style from './mediacard.module.scss';

const MediaCard = React.memo(({ 
  media, 
  title = '',
  width = '100%',
  isVideo = false // Add explicit isVideo prop
}) => {
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [isMediaReady, setIsMediaReady] = useState(false);
  const [mediaType, setMediaType] = useState(isVideo ? 'video' : 'image');
  const mediaRef = useRef(null);
  const { settings: { contrast } } = useAccessibility();

  useEffect(() => {
    if (!media) {
      setIsMediaReady(false);
      return;
    }

    // Only detect media type if not explicitly set
    if (!isVideo) {
      const detectedIsVideo = /\.(mp4|webm|ogg)$/i.test(media) || 
                            media.includes('video') || 
                            media.includes('mp4?');
      setMediaType(detectedIsVideo ? 'video' : 'image');
    }

    const checkMediaLoad = () => {
      if (mediaType === 'video') {
        const video = document.createElement('video');
        video.src = media;
        video.onloadedmetadata = () => {
          setIsMediaReady(true);
          URL.revokeObjectURL(video.src);
        };
        video.onerror = () => {
          console.error('Failed to load video:', media);
          setIsMediaReady(false);
          URL.revokeObjectURL(video.src);
        };
      } else {
        const img = new Image();
        img.src = media;
        img.onload = () => {
          setIsMediaReady(true);
          URL.revokeObjectURL(img.src);
        };
        img.onerror = () => {
          console.error('Failed to load image:', media);
          setIsMediaReady(false);
          URL.revokeObjectURL(img.src);
        };
      }
    };

    checkMediaLoad();
  }, [media, mediaType, isVideo]);

  const handleClick = () => {
    if (isMediaReady) {
      setIsLightboxOpen(true);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  const cardStyle = {
    width,
    '--card-bg': contrast !== 'default' ? 'var(--bg-primary)' : '#fff',
    cursor: isMediaReady ? 'pointer' : 'default'
  };

  if (!media) return null;

  return (
    <>
      <figure
        className={style.mediacard}
        style={cardStyle}
        onClick={isMediaReady ? handleClick : undefined}
        onKeyDown={isMediaReady ? handleKeyDown : undefined}
        role={isMediaReady ? "button" : undefined}
        aria-label={isMediaReady ? `View ${mediaType}: ${title}` : undefined}
        tabIndex={isMediaReady ? 0 : undefined}
      >
        <div className={style.wrapper}>
          {mediaType === 'video' ? (
            <>
              <video
                ref={mediaRef}
                className={style.media}
                src={media}
                aria-label={title}
                muted
                loop
                playsInline
                preload="metadata"
              />
              <div className={style.playButton}>
                <svg viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
            </>
          ) : (
            <img
              ref={mediaRef}
              className={`${style.media} ${style.mediaImage}`}
              src={media}
              alt={title}
              loading="lazy"
              decoding="async"
              style={{ opacity: isMediaReady ? 1 : 0 }}
            />
          )}
          {!isMediaReady && (
            <div className={style.loadingOverlay}>
              <div className={style.spinner}></div>
            </div>
          )}
        </div>
      </figure>

      {isLightboxOpen && isMediaReady && (
        mediaType === 'image' ? (
          <Lightbox
            mainSrc={media}
            onCloseRequest={() => setIsLightboxOpen(false)}
            imageTitle={title}
            imageCaption={title}
            enableZoom={true}
            clickOutsideToClose={true}
          />
        ) : (
          <div className={style.customLightbox}>
            <div className={style.lightboxContent}>
              <video
                className={style.lightboxVideo}
                src={media}
                controls
                autoPlay
              />
              {title && (
                <div className={style.lightboxCaption}>{title}</div>
              )}
            </div>
            <button
              className={style.lightboxClose}
              onClick={() => setIsLightboxOpen(false)}
              aria-label="Close lightbox"
            >
              ×
            </button>
          </div>
        )
      )}
    </>
  );
});

export default MediaCard;